import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Canvas, FabricObject } from 'fabric';
import { useProductSelectionStore } from './productSelectionStore';
import {
  useCanvasStateManager,
  type ProductDesignCrop,
  type PositionKey,
} from './canvasStateManager';
import { useUIStore } from './uiStore';
import { useGuidedEntryStore, type GuidedSide } from './guidedEntryStore';
import * as designsApi from '@/api/designs';
import type {
  CalibrationBlocker,
  DesignChannelPublication,
} from '@/api/designs';
import * as productsApi from '@/api/products';
import { queryClient } from '@/lib/queryClient';
import { productKeys } from '@/hooks/useProducts';
import { designKeys } from '@/hooks/useDesigns';
import type { Design, DesignStatus, PositionConfig } from '@/api/designs';
import { calculateSizeInfo, type SizeInfo } from '@/utils/sizeCalculation';
import { uploadDesignCrops, type CropUploadInput } from '@/utils/cropUpload';
import { loadImage } from '@/utils/mockupLayers';
import { ensureCanvasFontsLoaded } from '@/utils/canvasFonts';
import {
  canvasJSONFromPlacementMm,
  carryOverPositionMm,
  effectiveSizeTier,
  isPlacementMm,
  placementAreaMmFromPrintArea,
  placementMmFromCanvasJson,
  placementMmToRelative,
  relativeToPlacementMm,
  rescaleCanvasObjectsMm,
  selectMasterProduct,
  type CarryOverAnchor,
  type PlacementAreaMm,
  type PlacementMm,
  type ProductClass,
  type SizeTier,
  type CanvasObjectJSON,
  type CreatePrintContextParams,
  type PrintContext,
  type RelativeDesignState,
} from '@designdeck/shared';
import {
  buildPrintContextParamsFromTemplate,
  configuredTemplateOf,
} from '@/lib/printContextBuilder';
import {
  placementAreaOfContext,
  positionFromMm,
  positionToMm,
  type PositionJson,
  type PositionMmPayload,
} from '@/lib/positionMm';
import { describeApiError, getApiErrorCode } from '@/lib/api-errors';
import i18n from '@/lib/i18n';

/** Print area fields of a design product's position as the API returns them. */
type StoredPrintArea = {
  maxWidthMm?: number | null;
  maxHeightMm?: number | null;
  sizeTier?: SizeTier | null;
  sizeTierOverride?: SizeTier | null;
  carryOverPercent?: number | null;
  carryOverAnchor?: CarryOverAnchor | null;
  carryOverTopOffsetMm?: number | null;
};

/** The catalogue product of a design product, as far as the rule reads it. */
type StoredProductFields = {
  productClass?: ProductClass | null;
  carryOverPercent?: number | null;
  carryOverAnchor?: CarryOverAnchor | null;
  carryOverTopOffsetMm?: number | null;
} | null;

/** A design product's row as the loader reads it (positions untyped). */
type LoadedDesignProduct = {
  productId: string;
  isEnabled: boolean;
  createdAt?: string;
  product?: StoredProductFields;
  positions?: ReadonlyArray<object> | null;
};

/** A loaded design position, as far as the loader reads it. */
type LoadedPosition = {
  canvasData?: Record<string, unknown> | null;
  placementMm?: unknown;
  designCropUrl?: string | null;
  isCustomPlacement?: boolean;
  placementPresetId?: string | null;
  threadMapping?: Record<string, string> | null;
  productPosition?: {
    id?: string;
    slug?: string;
    printArea?: StoredPrintArea | null;
  } | null;
};

/** The position fields of a loaded design product row. */
function positionsOf(product: LoadedDesignProduct | null | undefined) {
  return (product?.positions ?? []) as ReadonlyArray<LoadedPosition>;
}

/** The placement area of a loaded position: its own mm, tier and settings. */
function loadedAreaOf(
  pos: LoadedPosition,
  product: LoadedDesignProduct
): PlacementAreaMm | null {
  return placementAreaMmFromPrintArea(pos.productPosition?.printArea, {
    id: product.productId,
    ...(product.product ?? {}),
  });
}

/** Why the loader skipped a position, for the console and the hint. */
function logSkippedPosition(
  productId: string,
  slug: string,
  reason: string
): void {
  console.error(
    `[designStore] position ${productId}/${slug} not opened: ${reason}`
  );
}

// Re-export DesignStatus for convenience
export type { DesignStatus } from '@/api/designs';

// ═══════════════════════════════════════════════════════════════════
// CANVAS SERIALIZATION CONFIG
// ═══════════════════════════════════════════════════════════════════

// NOTE: Custom properties are now configured globally in config/fabricConfig.ts
// Fabric.js 6.x requires FabricObject.customProperties to be set before object creation.
// The import in main.tsx ensures this happens at app startup.

/**
 * Build enriched product config with per-product position mapping.
 *
 * Instead of taking productPositionId from canvasStateManager (which only
 * holds the last-focused product's IDs), this resolves the correct
 * ProductPosition.id for each product from the API product data.
 *
 * @param productConfig - Product selection config from productSelectionStore
 * @param allPositionData - Canvas JSON keyed by position slug from canvasStateManager
 * @param apiProducts - Full product data from products API (includes positions)
 */
/** An embroidery motif carries the storage key of the image it was cut from. */
function holdsEmbroideryMotif(canvasData: unknown): boolean {
  const objects = (canvasData as { objects?: unknown[] } | null | undefined)
    ?.objects;
  return (
    Array.isArray(objects) &&
    objects.some(
      (object) =>
        typeof (object as { embFileKey?: unknown })?.embFileKey === 'string'
    )
  );
}

/** The storage key of the embroidery motif on a stored canvas, if any. */
function embroideryKeyOf(canvasData: unknown): string | null {
  const objects = (canvasData as { objects?: unknown[] } | null | undefined)
    ?.objects;
  if (!Array.isArray(objects)) return null;
  for (const object of objects) {
    const key = (object as { embFileKey?: unknown })?.embFileKey;
    if (typeof key === 'string') return key;
  }
  return null;
}

/** A master position as the canvas manager hands it to a save. */
type SavePositionData = {
  json: unknown;
  printContext: PrintContext;
  /** Effective crop source (DataURL or S3 URL) for persist */
  designCropUrl?: string | null;
  /** Guided entry authoring intent (see canvasStateManager.PositionState) */
  placementPresetId?: string | null;
};

/** The canvas JSON with the given objects and the placement they span. */
function rowOf(
  base: PositionJson,
  objects: CanvasObjectJSON[],
  area: PlacementAreaMm,
  fallback: PlacementMm
): { canvasData: Record<string, unknown>; placementMm: PlacementMm } {
  // The stored placement is the bounding box of the stored JSON (R35).
  return {
    canvasData: { ...base, objects } as Record<string, unknown>,
    placementMm: placementMmFromCanvasJson({ objects }, area) ?? fallback,
  };
}

/**
 * Build the product rows of a full save, in millimetres (R17, R34).
 *
 * The master positions come from the canvas manager on the area of the
 * template they were edited on; each is converted once (`positionToMm`).
 * Every product row then gets the master row on its own area: the master
 * product itself as is, a Custom Placement at its own placement with the
 * master objects moved there uniformly, every other product through the
 * carry-over rule (`carryOverPositionMm`, S2). The server derives the
 * non-custom rows from the master again on every master write.
 *
 * A product with its own canvas (single-product editor) sends only its
 * crop: its row keeps its own JSON and placement (patch semantics).
 */
function buildEnrichedProductConfig(
  productConfig: Array<{
    productId: string;
    isEnabled: boolean;
    /** Absent when the seller has not priced the product (never 0) */
    sellingPrice?: number;
    /** Absent for an inactive row: the server keeps its colours (patch semantics) */
    colors?: Array<{ productColorId: string }>;
  }>,
  allPositionData: Record<string, SavePositionData>,
  apiProducts: productsApi.Product[],
  /** Per-product placement states from canvasStateManager (custom placements only) */
  placementsByProduct: Record<string, Record<string, RelativeDesignState>> = {},
  /** Per-product independent design data (products with their own canvas) */
  productDesignCropsMap: Record<string, Record<string, ProductDesignCrop>> = {}
) {
  const masters = new Map<
    string,
    { area: PlacementAreaMm; row: PositionMmPayload }
  >();
  for (const [slug, data] of Object.entries(allPositionData)) {
    if (!data.json) continue;
    masters.set(slug, {
      area: placementAreaOfContext(data.printContext),
      row: positionToMm(data.json as PositionJson, data.printContext),
    });
  }

  return productConfig.map((config) => {
    const product = apiProducts.find((p) => p.id === config.productId);
    const productPositions = product?.positions ?? [];

    const productPlacements = placementsByProduct[config.productId] ?? {};
    const independentCrops = productDesignCropsMap[config.productId];

    const positions: PositionConfig[] = productPositions.flatMap(
      (pp): PositionConfig[] => {
        // A product with its own design keeps its row; only the crop the
        // cards show is refreshed.
        const independentCrop = independentCrops?.[pp.slug];
        if (independentCrop?.relativeState && independentCrop.cropUrl) {
          return [
            {
              productPositionId: pp.id,
              designCropUrl: independentCrop.cropUrl,
              isCustomPlacement: true,
            },
          ];
        }

        const posData = allPositionData[pp.slug];
        const master = masters.get(pp.slug);
        if (!posData || !master || !product) return [];

        const targetArea = placementAreaMmFromPrintArea(pp.printArea, product);
        if (!targetArea) {
          // An area without millimetres is not configured (R31): nothing
          // can be placed on it.
          console.error(
            `[designStore] ${product.id}/${pp.slug}: print area without mm, position not saved`
          );
          return [];
        }

        // An embroidered motif is stitched with the position's embroidery
        // method. Recorded on the DesignPosition so the server's publish rule
        // knows the position is embroidered — and so production does.
        //
        // A guided design says so explicitly either way: switching the run
        // from embroidery to print on the same design must clear the method,
        // or the server keeps judging the position as embroidered and refuses
        // the print. A classic position is left as it is — its method may
        // have been set by another author (the MCP path writes it).
        const embroideryMethodId = holdsEmbroideryMotif(posData.json)
          ? pp.availableDecorationMethods?.find(
              (method) => method.code === 'EMBROIDERY'
            )?.id
          : undefined;
        const decorationMethodId =
          embroideryMethodId ??
          (useUIStore.getState().creatorEntryMode === 'guided'
            ? null
            : undefined);

        const common = {
          productPositionId: pp.id,
          designCropUrl: posData.designCropUrl ?? undefined,
          // Guided entry authoring intent; null reads as "canvas-authored".
          placementPresetId: posData.placementPresetId ?? null,
          ...(decorationMethodId !== undefined && { decorationMethodId }),
        };

        const masterPlacement = master.row.placementMm;
        if (!masterPlacement) {
          // An emptied position: the empty JSON clears the placement.
          return [
            {
              ...common,
              canvasData: master.row.canvasData as Record<string, unknown>,
              isCustomPlacement: false,
            },
          ];
        }

        const masterObjects = (master.row.canvasData.objects ??
          []) as CanvasObjectJSON[];
        const custom = productPlacements[pp.slug];
        if (custom) {
          const placementMm = relativeToPlacementMm(
            custom,
            targetArea,
            masterPlacement.rotation
          );
          return [
            {
              ...common,
              ...rowOf(
                master.row.canvasData,
                rescaleCanvasObjectsMm(
                  masterObjects,
                  master.area,
                  targetArea,
                  placementMm
                ),
                targetArea,
                placementMm
              ),
              isCustomPlacement: true,
            },
          ];
        }

        if (product.id === master.area.productId) {
          // The product the master was edited on: its own row.
          return [
            {
              ...common,
              canvasData: master.row.canvasData as Record<string, unknown>,
              placementMm: masterPlacement,
              isCustomPlacement: false,
            },
          ];
        }

        const derived = carryOverPositionMm(
          {
            placementMm: masterPlacement,
            canvasData: master.row.canvasData,
          },
          master.area,
          targetArea
        );
        return [
          {
            ...common,
            ...rowOf(
              master.row.canvasData,
              (derived.canvasData?.objects ?? []) as CanvasObjectJSON[],
              targetArea,
              derived.placementMm
            ),
            isCustomPlacement: false,
          },
        ];
      }
    );

    return {
      ...config,
      positions,
    };
  });
}

/**
 * The design-level canvas JSON: the active master position in millimetre
 * space (R34), empty without one.
 */
function designLevelCanvas(): Record<string, unknown> {
  const { masterPositions, activePosition } = useCanvasStateManager.getState();
  const state = masterPositions[activePosition];
  if (!state?.json) return { version: '6.0.0', objects: [] };
  return positionToMm(state.json as PositionJson, state.printContext)
    .canvasData as Record<string, unknown>;
}

/**
 * Master product selection for loading: the shared rule from
 * `@designdeck/shared` (persisted reference first, then no Custom Placement,
 * then by what the others would inherit: textile without percent, textile
 * with percent, object, KLEIN/RUNDUM-only last; oldest first) applied to the
 * design's products, with each position's effective size tier and percent
 * read from its print area and the class from the catalogue product.
 */
function pickMasterProduct<T extends LoadedDesignProduct>(
  products: T[],
  options: { forceProductId?: string | null; masterProductId?: string | null }
): T | null {
  type LoosePosition = {
    placementMm?: unknown;
    isCustomPlacement?: boolean | null;
    productPosition?: { printArea?: StoredPrintArea | null } | null;
  };
  const chosen = selectMasterProduct(
    products.map((p) => ({
      productId: p.productId,
      isEnabled: p.isEnabled,
      createdAt: p.createdAt,
      productClass: p.product?.productClass,
      carryOverPercent: p.product?.carryOverPercent,
      positions: p.positions?.map((raw) => {
        const pos = raw as LoosePosition;
        return {
          placementMm: pos.placementMm ?? null,
          isCustomPlacement: pos.isCustomPlacement,
          sizeTier: pos.productPosition?.printArea
            ? effectiveSizeTier(pos.productPosition.printArea)
            : undefined,
          carryOverPercent: pos.productPosition?.printArea?.carryOverPercent,
        };
      }),
    })),
    options
  );
  if (!chosen) return null;
  return products.find((p) => p.productId === chosen.productId) ?? null;
}

/**
 * After the first save the server holds the master reference; mirror it
 * into both stores so later saves and the anchor rule agree with it.
 */
function rememberMasterAfterSave(masterProductId: string | undefined): void {
  if (!masterProductId || useDesignStore.getState().masterProductId) return;
  useDesignStore.setState({ masterProductId });
  useCanvasStateManager.getState().setMasterProduct(masterProductId);
}

/**
 * The master reference sent with a save: the loaded master when the design
 * has one, otherwise the focused product (the one the seller designs on).
 * The server records it once and never moves it (KTD3).
 */
function resolveMasterForSave(): string | undefined {
  return (
    useDesignStore.getState().masterProductId ??
    useProductSelectionStore.getState().focusedProductId ??
    undefined
  );
}

/**
 * The products a save writes a row for: the master, the active selection and
 * every product with a custom placement or an independent crop. The rest of
 * the catalogue is left out; the server deactivates rows it does not receive
 * and never deletes one, so a deselected product keeps its row, its colours
 * and its custom placement (R1-R5).
 */
function collectProductIdsForSave(): string[] {
  const ids = new Set<string>();

  const master = resolveMasterForSave();
  if (master) ids.add(master);

  const { selections } = useProductSelectionStore.getState();
  for (const [productId, selection] of Object.entries(selections)) {
    if (selection.isActive) ids.add(productId);
  }

  // Both maps drop a product's key once its last override is gone, so a key
  // is a reliable "has a custom placement" signal.
  const { placementsByProduct, productDesignCrops } =
    useCanvasStateManager.getState();
  for (const productId of Object.keys(placementsByProduct)) ids.add(productId);
  for (const productId of Object.keys(productDesignCrops)) ids.add(productId);

  return [...ids];
}

/**
 * The catalogue a save needs to map position slugs to ProductPosition ids.
 * Read from the query cache the creator already filled so a background save
 * does not pull the whole catalogue again; fetched only when the cache holds
 * nothing.
 */
function loadCatalogueForSave(): Promise<productsApi.Product[]> {
  // fetchQuery serves fresh cached data, fetches once on a miss and lets
  // concurrent saves share that one request.
  return queryClient.fetchQuery({
    queryKey: productKeys.list(undefined),
    queryFn: () => productsApi.getProducts(),
  });
}

/**
 * After a successful configuration save: fetch the fresh pricing quote and
 * seed the products the seller activated in this session with their
 * suggestedStartPrice (plan KTD9 — Web and MCP seed by the same rule). A
 * failed quote never fails the save; the card shows its own error state.
 */
async function adoptPricingAfterSave(designId: string): Promise<void> {
  try {
    const pricing = await queryClient.fetchQuery({
      queryKey: designKeys.pricing(designId),
      queryFn: () => designsApi.getDesignPricing(designId),
      staleTime: 0,
    });
    const adopted = useProductSelectionStore
      .getState()
      .adoptSuggestedPrices(pricing?.products ?? []);
    if (adopted > 0) {
      // The seeded prices are not on the server yet; the next save carries them.
      useDesignStore.getState().setHasUnsavedChanges(true);
    }
  } catch (error) {
    console.warn(
      `[designStore] pricing quote after save failed for ${designId}:`,
      error
    );
  }
}

/**
 * Default canvas dimensions for print area
 */
export const DEFAULT_CANVAS_DIMENSIONS = {
  width: 280,
  height: 350,
};

/**
 * Print Area definition
 */
export interface PrintArea {
  x: number;
  y: number;
  width: number;
  height: number;
  bleed: number;
}

/**
 * Design Store Interface
 */
interface DesignStore {
  // Canvas Reference
  canvas: Canvas | null;
  setCanvas: (canvas: Canvas | null) => void;

  // Design Metadata
  designId: string | null;
  /**
   * Empty until the design has a name. The "untitled" default is resolved in
   * the shop language when the design is created, never at module load.
   */
  designName: string;
  designDescription: string;
  status: DesignStatus;
  /** Product ID whose positions are loaded into canvasStateManager (source of truth) */
  masterProductId: string | null;
  /**
   * Sales channel persisted on the loaded design, null when it has none.
   * Read by the publish step in an impersonation tab, which has no iframe
   * URL to take the channel from (see usePublishDesign).
   */
  channelName: string | null;
  /** Where the loaded design already sits; the publish step names it. */
  channelPublications: DesignChannelPublication[];
  /**
   * True while a channel job publishes the loaded design. Saves are skipped
   * meanwhile (see saveDesign / useAutoSave); the server refuses them too.
   */
  publishRunning: boolean;
  /**
   * Calibration reasons the server reported for the loaded design (R31);
   * the publish step filters them by the current selection.
   */
  calibrationBlockers: CalibrationBlocker[];

  // Backend Sync State
  isLoading: boolean;
  isSaving: boolean;
  lastSavedAt: Date | null;
  syncError: string | null;

  // Selection
  selectedObjects: FabricObject[];
  setSelectedObjects: (objects: FabricObject[]) => void;

  // Size Info (reactive - updated on every object change)
  sizeInfo: SizeInfo | null;
  updateSizeInfo: () => void;
  clearSizeInfo: () => void;

  // Print Area
  printArea: PrintArea | null;
  setPrintArea: (area: PrintArea | null) => void;

  // Dirty State
  hasUnsavedChanges: boolean;
  /**
   * Advances with every change that marks the design dirty. A save pass reads
   * it at its start and clears `hasUnsavedChanges` at its end only if the
   * counter still matches, so a change made mid-flight stays unsaved.
   */
  changeVersion: number;
  /**
   * The `changeVersion` of the last save the server refused as invalid
   * (400/422, e.g. a price below the seller cost). Resending the same state
   * cannot succeed, so the autosave waits for the next change instead of
   * retrying every few seconds; null when the last save did not fail so.
   */
  rejectedAtVersion: number | null;
  setHasUnsavedChanges: (value: boolean) => void;

  // Actions
  setDesignId: (id: string | null) => void;
  setDesignName: (name: string) => void;
  setDesignDescription: (description: string) => void;
  setStatus: (status: DesignStatus) => void;
  /**
   * Take over a status the server already holds (or is about to, see
   * usePublishDesign). Unlike `setStatus` this neither marks the design
   * dirty nor advances `changeVersion`, so no save follows from it.
   */
  adoptStatus: (status: DesignStatus) => void;
  clearDesign: () => void;
  saveToHistory: (action?: string) => void;

  // Canvas Helpers
  getCanvasDimensions: () => { width: number; height: number };

  // Backend Sync Actions
  loadDesign: (id: string) => Promise<Design | null>;
  saveDesign: () => Promise<Design | null>;
  createDesign: (name?: string) => Promise<Design | null>;
  deleteDesign: () => Promise<boolean>;

  /**
   * Save changes for a single product only (single-product edit mode).
   *
   * - Saves position data (canvasData and placementMm in millimetres, crop)
   *   ONLY for the target product via per-position PATCH requests.
   * - Saves metadata (name, description, status) via updateDesign()
   *   WITHOUT overwriting Design.canvasData (master).
   * - Does NOT touch other products' DesignPosition records.
   */
  saveSingleProduct: () => Promise<Design | null>;

  // Thumbnail. `thumbnail` is the capture a save pass started while the
  // canvas was still mounted; without it the canvas must be mounted now.
  uploadThumbnail: (
    designId: string,
    thumbnail?: Promise<Blob | null>
  ) => Promise<void>;
}

/**
 * Design Store - manages canvas state and design metadata
 */
/**
 * Upper bound for a coalesced caller waiting on a running save. A pass that
 * never settles (a request that hangs) must not keep a step change, "Edit
 * placement" or leaving the creator waiting forever.
 */
export const SAVE_WAIT_TIMEOUT_MS = 45_000;

type WaitOutcome = 'idle' | 'timeout';

/**
 * Resolves once no save is running, or with 'timeout' after the upper bound.
 * `isSaving` is the one flag every save path sets and clears, so waiting on
 * it serializes them without a second source of truth.
 */
function waitWhileSaving(
  timeoutMs: number = SAVE_WAIT_TIMEOUT_MS
): Promise<WaitOutcome> {
  if (!useDesignStore.getState().isSaving) return Promise.resolve('idle');

  return new Promise((resolve) => {
    // The timer fires after this function returned, so `unsubscribe` is
    // assigned by then even though it is declared below.
    const timer = setTimeout(() => {
      unsubscribe();
      resolve('timeout');
    }, timeoutMs);
    const unsubscribe = useDesignStore.subscribe((state) => {
      if (!state.isSaving) {
        clearTimeout(timer);
        unsubscribe();
        resolve('idle');
      }
    });
  });
}

/** The save entry points that coalesce; each keeps its own trailing pass. */
type SaveEntryPoint = 'saveDesign' | 'saveSingleProduct' | 'createDesign';

/** One pending trailing pass per save entry point, keyed by its name. */
const trailingSaves = new Map<string, Promise<Design | null>>();

/** The pass holding the save lock and the change counter it captured. */
interface RunningSave {
  promise: Promise<Design | null>;
  changeVersion: number;
}

let runningSave: RunningSave | null = null;

/**
 * Ownership token for the pass that holds the save lock. Each pass takes
 * the next number on entry; the wait timeout takes one too, so a pass that
 * outlived it is superseded and must not write its ending (isSaving,
 * hasUnsavedChanges, lastSavedAt, syncError, thumbnail) over the successor
 * that started after the lock was freed.
 */
let savePassSeq = 0;

/**
 * Registers a pass as the running one so a coalesced caller can hand back
 * its result when nothing changed in the meantime. Cleared once the pass
 * settles; a pass that outlived a wait timeout must not clear its successor.
 */
function trackRunningSave(
  changeVersion: number,
  pass: Promise<Design | null>
): Promise<Design | null> {
  const entry: RunningSave = { promise: pass, changeVersion };
  runningSave = entry;
  return pass.finally(() => {
    if (runningSave === entry) runningSave = null;
  });
}

/**
 * Starts the thumbnail capture while the canvas is still mounted. `toBlob`
 * reads the element synchronously, so the promise survives a step change
 * that disposes the canvas before the save chain finishes.
 */
function captureThumbnail(canvas: Canvas): Promise<Blob | null> {
  try {
    const element = canvas.getElement();
    if (!element) return Promise.resolve(null);
    return new Promise((resolve) => {
      element.toBlob((blob) => resolve(blob), 'image/webp', 0.8);
    });
  } catch {
    // No element or no toBlob (a disposed canvas, a test double): no thumbnail.
    return Promise.resolve(null);
  }
}

/**
 * The newest capture a coalesced caller made for a design. Its trailing pass
 * usually runs after the step change disposed the canvas, so the pass takes
 * this capture instead of a canvas that no longer exists.
 */
let queuedThumbnail: {
  designId: string;
  thumbnail: Promise<Blob | null>;
} | null = null;

/**
 * The thumbnail a save pass uploads at its end: a fresh capture while the
 * canvas is mounted, otherwise the capture a coalesced caller left behind
 * for this design. Undefined means "no thumbnail this time".
 */
function captureThumbnailForSave(
  canvas: Canvas | null,
  designId: string
): Promise<Blob | null> | undefined {
  if (canvas) {
    queuedThumbnail = null;
    return captureThumbnail(canvas);
  }
  if (queuedThumbnail?.designId === designId) {
    const { thumbnail } = queuedThumbnail;
    queuedThumbnail = null;
    return thumbnail;
  }
  return undefined;
}

/** Keeps a coalesced caller's capture for the trailing pass of this design. */
function queueThumbnailForTrailingSave(
  designId: string,
  thumbnail: Promise<Blob | null> | undefined
): void {
  if (thumbnail) queuedThumbnail = { designId, thumbnail };
}

/**
 * Test seam: the coalescing state outlives a store reset, so tests must be
 * able to clear it.
 */
export function __resetTrailingSaves(): void {
  trailingSaves.clear();
  runningSave = null;
  savePassSeq = 0;
  queuedThumbnail = null;
}

/**
 * A save requested while another one runs must not be dropped: the running
 * pass serialized the canvas before that change existed, so returning null
 * loses it (the caller reads "nothing to save" and moves on - step switch,
 * "Edit placement", auto-save). Concurrent callers instead coalesce onto a
 * single trailing pass that starts once the lock frees, so the newest state
 * reaches the server and every caller gets the saved design back.
 */
function coalesceAfterSave(
  key: SaveEntryPoint,
  run: () => Promise<Design | null>
): Promise<Design | null> {
  const pending = trailingSaves.get(key);
  if (pending) return pending;

  // The pass runs later, against whatever the store holds by then. Snapshot
  // the context it was queued for: a design switch (loadDesign/clearDesign)
  // or leaving single-product mode must abandon it, not write this canvas
  // into a different design.
  const queuedDesignId = useDesignStore.getState().designId;
  const queuedSingleProductMode = useUIStore.getState().singleProductMode;
  // The pass this caller waits on. Its result stands in for the trailing
  // pass when the change counter has not moved since it read its inputs.
  const running = runningSave;

  const trailing = waitWhileSaving().then((outcome) => {
    // Dropped before the run: a change made during the trailing pass has to
    // queue its own pass rather than ride on this one.
    trailingSaves.delete(key);

    if (outcome === 'timeout') {
      console.error(
        `[designStore] ${key}: gave up waiting for the running save after ${SAVE_WAIT_TIMEOUT_MS} ms`
      );
      // Resolve, never reject: every caller reads null as "save failed" and
      // the auto-save timer awaits without a try/catch. Freeing the lock
      // lets the next save start instead of queueing behind the stuck one;
      // the stuck pass is superseded first so its late ending stays out of
      // the successor's state.
      savePassSeq += 1;
      useDesignStore.setState({
        syncError: i18n.t('creator.toast.saveTimeout'),
        isSaving: false,
      });
      return null;
    }

    const { designId, changeVersion } = useDesignStore.getState();
    const { singleProductMode } = useUIStore.getState();

    if (
      designId !== queuedDesignId ||
      singleProductMode?.productId !== queuedSingleProductMode?.productId
    ) {
      // Not an error - the caller's context is simply gone. Returning null
      // without touching syncError keeps the auto-save toast quiet.
      console.warn(
        `[designStore] ${key}: context changed while queued, dropping trailing pass`
      );
      return null;
    }

    if (running && changeVersion === running.changeVersion) {
      // Nothing changed since the running pass read its inputs: its result
      // is this caller's result, and an identical second request is spared.
      return running.promise;
    }

    return run();
  });

  trailingSaves.set(key, trailing);
  return trailing;
}

/**
 * Resolves 'settled' once the promise settles (either way — the caller
 * re-checks the store and does not care why a pass ended), or 'timeout'
 * once `ms` elapsed first.
 */
function settleWithin(
  promise: Promise<unknown>,
  ms: number
): Promise<'settled' | 'timeout'> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve('timeout'), ms);
    const done = () => {
      clearTimeout(timer);
      resolve('settled');
    };
    promise.then(done, done);
  });
}

/**
 * Resolves once no save runs and no trailing pass is queued, or with
 * 'timeout' after SAVE_WAIT_TIMEOUT_MS overall. `isSaving` alone is not
 * enough: a coalesced caller's pass sits in `trailingSaves` and starts only
 * after the lock frees, so a caller that read `isSaving === false` between
 * the two would race the trailing pass. Touches no state on timeout — the
 * caller decides whether to abort or proceed.
 */
export async function awaitPendingSaves(): Promise<WaitOutcome> {
  const deadline = Date.now() + SAVE_WAIT_TIMEOUT_MS;

  for (;;) {
    const remaining = deadline - Date.now();
    if (remaining <= 0) return 'timeout';

    if (useDesignStore.getState().isSaving) {
      if ((await waitWhileSaving(remaining)) === 'timeout') return 'timeout';
      continue;
    }

    const trailing = Array.from(trailingSaves.values());
    if (trailing.length === 0) return 'idle';

    // The trailing pass sets `isSaving` when it starts; the loop re-checks.
    const outcome = await settleWithin(Promise.all(trailing), remaining);
    if (outcome === 'timeout') return 'timeout';
  }
}

/** The state patch that marks the design dirty and advances the counter. */
const markChanged = (state: Pick<DesignStore, 'changeVersion'>) => ({
  hasUnsavedChanges: true,
  changeVersion: state.changeVersion + 1,
});

export const useDesignStore = create<DesignStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      canvas: null,
      designId: null,
      // Empty until the design has a name; createDesign fills in the
      // "untitled" default in the shop language at that moment.
      designName: '',
      designDescription: '',
      status: 'DRAFT' as DesignStatus,
      masterProductId: null,
      channelName: null,
      channelPublications: [],
      publishRunning: false,
      calibrationBlockers: [],
      selectedObjects: [],
      sizeInfo: null,
      printArea: null,
      hasUnsavedChanges: false,
      changeVersion: 0,
      rejectedAtVersion: null,

      // Backend Sync State
      isLoading: false,
      isSaving: false,
      lastSavedAt: null,
      syncError: null,

      // Canvas Reference
      setCanvas: (canvas) => set({ canvas }),

      // Selection
      setSelectedObjects: (objects) => set({ selectedObjects: objects }),

      // Size Info - Reactive updates for selected object dimensions
      updateSizeInfo: () => {
        const { canvas } = get();
        if (!canvas) {
          set({ sizeInfo: null });
          return;
        }

        const activeObject = canvas.getActiveObject();
        if (!activeObject) {
          set({ sizeInfo: null });
          return;
        }

        // Get pxPerMm from PrintContext - Single Source of Truth
        const printContext =
          useProductSelectionStore.getState().activePrintContext;
        if (!printContext) {
          set({ sizeInfo: null });
          return;
        }
        const { pxPerMm } = printContext;

        // Get locale from browser
        const locale =
          typeof navigator !== 'undefined' ? navigator.language : 'en';

        // Calculate and set size info
        const info = calculateSizeInfo(activeObject, pxPerMm, locale);
        set({ sizeInfo: info });
      },

      clearSizeInfo: () => set({ sizeInfo: null }),

      // Print Area
      setPrintArea: (area) => set({ printArea: area }),

      // Dirty State. Clearing the flag is not a change; only setting it
      // advances the counter a running save compares against.
      setHasUnsavedChanges: (value) =>
        set((state) =>
          value ? markChanged(state) : { hasUnsavedChanges: false }
        ),

      // Design Metadata
      setDesignId: (id) => set({ designId: id }),

      setDesignName: (name) => {
        set((state) => ({ designName: name, ...markChanged(state) }));
      },

      setDesignDescription: (description) => {
        set((state) => ({
          designDescription: description,
          ...markChanged(state),
        }));
      },

      setStatus: (status) => {
        set((state) => ({ status, ...markChanged(state) }));
      },

      adoptStatus: (status) => {
        set({ status });
      },

      clearDesign: () => {
        const { canvas } = get();
        if (canvas) {
          canvas.clear();
          canvas.backgroundColor = 'transparent';
          canvas.requestRenderAll();
        }
        useCanvasStateManager.getState().clearHistory();
        // Clear all position canvas data
        useCanvasStateManager.getState().clearAllPositions();
        set({
          designId: null,
          designName: '',
          designDescription: '',
          status: 'DRAFT',
          masterProductId: null,
          channelName: null,
          channelPublications: [],
          publishRunning: false,
          calibrationBlockers: [],
          selectedObjects: [],
          sizeInfo: null,
          printArea: null,
          hasUnsavedChanges: false,
          lastSavedAt: null,
          syncError: null,
          rejectedAtVersion: null,
        });
      },

      saveToHistory: (action = 'unknown') => {
        const { canvas } = get();
        if (canvas) {
          useCanvasStateManager.getState().saveToHistory(action);
          set((state) => markChanged(state));
        }
      },

      getCanvasDimensions: () => {
        // Return fixed print area dimensions
        return DEFAULT_CANVAS_DIMENSIONS;
      },

      // ═══════════════════════════════════════════════════════════════════
      // BACKEND SYNC ACTIONS
      // ═══════════════════════════════════════════════════════════════════

      /**
       * Load a design from the backend
       *
       * WICHTIG: Reihenfolge ist kritisch!
       * 1. Produkt-Konfiguration ZUERST laden (setzt korrekten PrintContext)
       * 2. Position-Daten in CanvasStateManager speichern
       * 3. KEIN applyDesignToCanvas() - CanvasEditor useEffect lädt automatisch
       *    wenn sich positions ändert und PrintContext korrekt gesetzt ist
       */
      loadDesign: async (id: string) => {
        set({ isLoading: true, syncError: null });
        try {
          // A reload during a running or queued save (entering and leaving
          // the single-product editor reloads the same design) would read
          // the server's state from before that save and then overwrite the
          // store with it. Wait for the save first; if it hangs past the
          // bound, load anyway rather than leaving the seller stuck.
          if ((await awaitPendingSaves()) === 'timeout') {
            console.warn(
              `[designStore] loadDesign(${id}): a save is still pending after ${SAVE_WAIT_TIMEOUT_MS} ms, loading anyway`
            );
          }

          const design = await designsApi.getDesign(id);

          // A switch anchor belongs to the design it was made in. Opening a
          // different design must not let the previous one's anchor resolve
          // the master state at the next save.
          const previousId = get().designId;
          if (previousId && previousId !== id) {
            useCanvasStateManager.getState().clearSwitchAnchors();
          }

          // 1. Clear existing position data
          useCanvasStateManager.getState().clearAllPositions();

          // 2. Restore product selection FIRST (sets correct PrintContext!)
          if (design.products && design.products.length > 0) {
            const productData = design.products.map((dp) => ({
              productId: dp.productId,
              isEnabled: dp.isEnabled,
              sellingPrice: dp.sellingPrice,
              colors: dp.colors?.map((c) => ({
                productColorId: c.productColorId,
              })),
            }));
            useProductSelectionStore.getState().loadFromDesign(productData);
          }

          // 3. Load position-specific canvas data into CanvasStateManager
          // Stufe 1.3: Master-Product Selektion + canvasData-Fallback
          //
          // IMPORTANT: We only load positions from ONE "master" product to avoid
          // slug collisions (e.g. two products both having "front"). The master
          // product is selected deterministically: non-custom positions first,
          // then oldest DesignProduct.createdAt, then lexicographic productId.
          const products = design.products ?? [];

          // Deterministic master selection (Stufe 1.3)
          // In single-product mode: force the specified product as master
          const { singleProductMode } = useUIStore.getState();
          const forceProductId = singleProductMode?.productId;
          const masterProduct = pickMasterProduct(products, {
            forceProductId,
            masterProductId: design.masterProductId,
          });
          // The design step edits the master; a stale focus from a previous
          // visit (persisted focusedProductId) would rescale the master onto
          // that product and shrink every preview. Focus the master first.
          // This must not depend on the master's front position having
          // canvasData (loadedFrontPos below) -- a master edited only on
          // "back" would otherwise never get this correction.
          // The canvas manager needs the master to tell a Klein edit on
          // another product from an edit of the master itself (KTD7).
          useCanvasStateManager
            .getState()
            .setMasterProduct(masterProduct?.productId ?? null);

          if (!singleProductMode && masterProduct) {
            const selection = useProductSelectionStore.getState();
            if (selection.focusedProductId !== masterProduct.productId) {
              const firstColor =
                masterProduct.colors?.[0]?.productColorId ?? null;
              selection.setFocusedProduct(masterProduct.productId, firstColor);
            }
          }

          // The catalogue's templates give each position its rectangle and
          // Pixel Scale (R18); the stored rows carry only millimetres.
          const catalogue = await loadCatalogueForSave();
          const catalogueProduct = (productId: string) =>
            catalogue.find((p) => p.id === productId);

          const allPositions: Array<{
            positionSlug: string;
            canvasData: Record<string, unknown>;
            productPositionId: string | null;
            printContext: CreatePrintContextParams;
            designCropUrl: string | null;
            placementPresetId: string | null;
            designWidthMm: number | null;
          }> = [];
          const skippedPositions: string[] = [];

          // Positions come from the master product only (no slug
          // collisions); in the single-product editor that is the edited
          // product, whose rows hold its own placement on its own area.
          const apiMaster = masterProduct
            ? catalogueProduct(masterProduct.productId)
            : undefined;
          for (const pos of masterProduct ? positionsOf(masterProduct) : []) {
            const slug = pos.productPosition?.slug;
            if (!slug || !masterProduct) continue;
            const cropUrl = pos.designCropUrl ?? null;
            const storedObjects = Array.isArray(
              (pos.canvasData as { objects?: unknown } | null | undefined)
                ?.objects
            )
              ? ((pos.canvasData as { objects: unknown[] }).objects ?? [])
              : [];
            if (storedObjects.length === 0 && !cropUrl) continue;

            const template = configuredTemplateOf(apiMaster, slug);
            const area = loadedAreaOf(pos, masterProduct);
            if (!template || !area) {
              // No calibrated template: the editor has no rectangle to put
              // the design on, and no default is invented (R13).
              logSkippedPosition(
                masterProduct.productId,
                slug,
                'no calibrated template'
              );
              skippedPositions.push(slug);
              continue;
            }
            const printContext = buildPrintContextParamsFromTemplate(template);

            // Rows written without a canvas (batch-apply, bulk) carry a
            // placement and a crop: the canvas is synthesized from both, in
            // millimetres like a stored row.
            let canvasData = pos.canvasData ?? null;
            if (
              storedObjects.length === 0 &&
              cropUrl &&
              isPlacementMm(pos.placementMm)
            ) {
              // Fabric cuts the object's source rectangle from the declared
              // width/height, so the crop's real pixel size has to be read
              // before the object can be built.
              try {
                const img = await loadImage(cropUrl);
                canvasData = canvasJSONFromPlacementMm(
                  pos.placementMm,
                  cropUrl,
                  { width: img.naturalWidth, height: img.naturalHeight }
                ) as unknown as Record<string, unknown>;
              } catch (error) {
                console.warn(
                  `[designStore] Could not read crop dimensions for ${slug}; position left without canvas data`,
                  error
                );
              }
            }

            const loaded = positionFromMm(
              canvasData as PositionJson | null,
              pos.placementMm,
              area,
              printContext.pxPerMm
            );
            if (loaded.kind === 'skipped') {
              logSkippedPosition(masterProduct.productId, slug, loaded.reason);
              skippedPositions.push(slug);
              continue;
            }
            const placementMm = pos.placementMm as PlacementMm;
            allPositions.push({
              positionSlug: slug,
              canvasData: loaded.json as Record<string, unknown>,
              productPositionId: pos.productPosition?.id ?? null,
              printContext,
              designCropUrl: cropUrl,
              placementPresetId: pos.placementPresetId ?? null,
              designWidthMm: Math.round(placementMm.width),
            });
          }

          if (allPositions.length > 0) {
            await useCanvasStateManager.getState().loadFromDesign(allPositions);
          }

          // Custom Placements and independent designs of the other
          // products, as the relative view of each row's placement on its
          // own area (R36).
          const masterProductId = masterProduct?.productId;
          const productPlacements = products.map((dp) => ({
            productId: dp.productId,
            positions: positionsOf(dp).flatMap((pos) => {
              const slug = pos.productPosition?.slug;
              const area = loadedAreaOf(pos, dp);
              if (!slug) return [];
              return [
                {
                  slug,
                  placement:
                    area && isPlacementMm(pos.placementMm)
                      ? placementMmToRelative(pos.placementMm, area)
                      : null,
                  isCustomPlacement: pos.isCustomPlacement,
                },
              ];
            }),
          }));
          useCanvasStateManager
            .getState()
            .hydrateProductPlacements(productPlacements);

          // Only an independently edited product (single-product editor,
          // isCustomPlacement) owns a design of its own. Batch-applied crops
          // of non-custom products are the master crop and must not turn the
          // product into a custom placement on the next save (see
          // docs/plans/2026-09-02-001-fix-placement-adjust-analysis.md, C).
          const designCrops: Record<
            string,
            Record<PositionKey, ProductDesignCrop>
          > = {};
          for (const dp of products) {
            if (dp.productId === masterProductId) continue;
            const bySlug: Record<PositionKey, ProductDesignCrop> = {};
            for (const entry of productPlacements.find(
              (p) => p.productId === dp.productId
            )?.positions ?? []) {
              const cropUrl =
                positionsOf(dp).find(
                  (pos) => pos.productPosition?.slug === entry.slug
                )?.designCropUrl ?? null;
              if (entry.isCustomPlacement && cropUrl && entry.placement) {
                bySlug[entry.slug] = {
                  cropUrl,
                  relativeState: entry.placement,
                };
              }
            }
            if (Object.keys(bySlug).length > 0) {
              designCrops[dp.productId] = bySlug;
            }
          }
          useCanvasStateManager
            .getState()
            .setAllProductDesignCrops(designCrops);

          // 4. The selection carries the focused product's front template,
          // so the canvas shows the area the seller sees. When that is not
          // the template the master JSON was converted for, the editor's
          // same-position switch moves the objects onto it via the anchor.
          const loadedFrontPos =
            useCanvasStateManager.getState().masterPositions['front'];
          if (loadedFrontPos?.printContext) {
            const focusedProductId =
              useProductSelectionStore.getState().focusedProductId;
            const focusedApi = focusedProductId
              ? catalogueProduct(focusedProductId)
              : undefined;
            const focusedTemplate = configuredTemplateOf(focusedApi, 'front');
            const focusedPositionId =
              focusedApi?.positions?.find((pp) => pp.slug === 'front')?.id ??
              null;
            if (!singleProductMode && focusedTemplate) {
              useProductSelectionStore
                .getState()
                .setActivePosition(
                  'front',
                  focusedPositionId,
                  buildPrintContextParamsFromTemplate(focusedTemplate)
                );
            } else {
              useProductSelectionStore
                .getState()
                .setActivePosition(
                  'front',
                  loadedFrontPos.productPositionId,
                  loadedFrontPos.printContext
                );
            }
          }

          // A position that could not be opened is named once (R13).
          if (skippedPositions.length > 0) {
            useUIStore.getState().showToast(
              i18n.t('creator.toast.positionsSkippedOnLoad', {
                count: skippedPositions.length,
              })
            );
          }

          // 5. Update store state
          set({
            designId: design.id,
            designName: design.name,
            designDescription: design.description || '',
            status: design.status as DesignStatus,
            masterProductId: masterProduct?.productId ?? null,
            channelName: design.channelName ?? null,
            channelPublications: design.channelPublications ?? [],
            publishRunning: design.publishRunning ?? false,
            calibrationBlockers: design.calibrationBlockers ?? [],
            hasUnsavedChanges: false,
            lastSavedAt: new Date(design.updatedAt),
            isLoading: false,
          });

          // 6. A guided design re-opens in the guided entry with its presets
          // and widths — the geometry alone could not tell a preset from a
          // free placement. Sides are hydrated from the master positions.
          if (design.createdVia === 'guided' && !singleProductMode) {
            // Whatever a previously opened design left in the guided store
            // must be gone before the step mounts, or its sync would write
            // those motifs into this design during the async hydrate.
            useGuidedEntryStore.getState().reset();
            useUIStore.getState().setCreatorEntryMode('guided');
            const sides = positionsOf(masterProduct).flatMap((pos) => {
              const slug = pos.productPosition?.slug;
              const cropUrl = pos.designCropUrl ?? null;
              const hasObjects =
                ((pos.canvasData as { objects?: unknown[] } | null)?.objects
                  ?.length ?? 0) > 0;
              if (
                (slug !== 'front' && slug !== 'back') ||
                !cropUrl ||
                !hasObjects ||
                !isPlacementMm(pos.placementMm)
              ) {
                return [];
              }
              return [
                {
                  side: slug as GuidedSide,
                  cropUrl,
                  placementPresetId: pos.placementPresetId ?? null,
                  // The seller saw millimetres: the stored box gives them back.
                  designWidthMm: Math.round(pos.placementMm.width),
                  referenceWidthMm:
                    pos.productPosition?.printArea?.maxWidthMm ?? null,
                  embFileKey: embroideryKeyOf(pos.canvasData),
                  threadMapping: pos.threadMapping ?? null,
                },
              ];
            });
            void useGuidedEntryStore
              .getState()
              .hydrateFromDesign(sides, masterProduct?.productId ?? null);
          }
          return design;
        } catch (error) {
          const message = describeApiError(error);
          set({ syncError: message, isLoading: false });
          return null;
        }
      },

      /**
       * Save current design to the backend
       * Uses Fabric.js canvasData as Source of Truth
       * Also saves product configuration and position-specific canvas data
       *
       * NOTE: This function works both with and without an active canvas:
       * - With canvas: Serializes current canvas state
       * - Without canvas: Uses already stored position data from canvasStateManager
       *   (e.g., when saving from Step 2 where canvas is not mounted)
       */
      saveDesign: async () => {
        // Delegate to saveSingleProduct() in single-product mode
        const { singleProductMode } = useUIStore.getState();
        if (singleProductMode) {
          return get().saveSingleProduct();
        }

        const { designId, designName, designDescription, canvas } = get();

        if (!designId) {
          // No design ID means we need to create first
          return get().createDesign(designName);
        }

        // Before the lock check, so a call that coalesces still leaves the
        // canvas state in masterPositions and a thumbnail behind: its
        // trailing pass usually runs after a step change disposed the canvas.
        if (canvas) {
          // The crop printed from is exported here; only yields when a
          // text's face is still loading (see ensureCanvasFontsLoaded).
          const fontsPending = ensureCanvasFontsLoaded(canvas);
          if (fontsPending) await fontsPending;
          useCanvasStateManager.getState().saveCurrentPosition();
        }
        const thumbnail = captureThumbnailForSave(canvas, designId);

        // A channel job is writing this listing to a shop: the server would
        // answer 409 and a save landing between two attempts would put mixed
        // versions into the shop. The changes stay unsaved in the store.
        if (get().publishRunning) {
          return null;
        }

        // Auto-save + manual save race: wait for the running pass, then save
        // once more so this call's changes are persisted too.
        if (get().isSaving) {
          queueThumbnailForTrailingSave(designId, thumbnail);
          return coalesceAfterSave('saveDesign', () => get().saveDesign());
        }

        // The counter at the start of the pass: the flag is cleared at the
        // end only if nothing changed while the requests were in flight.
        const capturedVersion = get().changeVersion;
        const token = ++savePassSeq;
        set({ isSaving: true, syncError: null });

        const pass = (async (): Promise<Design | null> => {
          try {
            // 1. Every payload input is read before the first await, so the
            // canvas JSON and the product selection come from one moment.
            const allPositionData = useCanvasStateManager
              .getState()
              .getAllPositionData();

            // 2. The design-level canvas: the active position in millimetres
            // (R34). saveCurrentPosition above already wrote the live canvas
            // into the position state, mounted or not.
            const canvasData = designLevelCanvas();

            // 3. The rows of the master, the active selection and the custom
            // placements, and the crops that still need an upload
            const csm = useCanvasStateManager.getState();
            const { placementsByProduct, productDesignCrops } = csm;
            const productIds = collectProductIdsForSave();
            const productConfig = useProductSelectionStore
              .getState()
              .getAllProductsConfig(productIds);

            const cropInputs: CropUploadInput[] = [];
            for (const [slug, posState] of Object.entries(
              csm.masterPositions
            )) {
              if (posState.designCropDataUrl && posState.json?.objects) {
                cropInputs.push({
                  positionSlug: slug,
                  designCropDataUrl: posState.designCropDataUrl,
                  canvasObjects: posState.json.objects as Array<
                    Record<string, unknown>
                  >,
                  lastUploadedCropHash: posState.lastUploadedCropHash,
                });
              }
            }

            // 4. Upload dirty design crops to S3 (Stufe 4.3)
            // Must happen BEFORE buildEnrichedProductConfig so S3 URLs
            // (not DataURLs) are sent to the backend.
            if (cropInputs.length > 0) {
              const cropResults = await uploadDesignCrops(cropInputs, designId);

              // Update position state with S3 URLs and hashes
              for (const result of cropResults) {
                useCanvasStateManager
                  .getState()
                  .setCropUploadResult(
                    result.positionSlug,
                    result.s3Url,
                    result.contentHash
                  );
              }

              // Re-read position data after crop uploads (now contains S3 URLs)
              const updatedPositionData = useCanvasStateManager
                .getState()
                .getAllPositionData();
              Object.assign(allPositionData, updatedPositionData);
            }

            // 5. Save the rows with position-specific canvas data. The rows
            // go first: they are the truth on load, so a failure here must
            // not leave a newer design canvas next to older rows.
            if (productConfig.length > 0) {
              const apiProducts = await loadCatalogueForSave();
              const enrichedProductConfig = buildEnrichedProductConfig(
                productConfig,
                allPositionData,
                apiProducts,
                placementsByProduct,
                productDesignCrops
              );

              // Full-Fail (Stufe 1.2): a failed configuration ends the save
              // before the metadata is written; nothing is partially saved.
              try {
                const masterForSave = resolveMasterForSave();
                await designsApi.saveDesignConfiguration(designId, {
                  products: enrichedProductConfig,
                  masterProductId: masterForSave,
                });
                rememberMasterAfterSave(masterForSave);
                await adoptPricingAfterSave(designId);

                // Stufe 4.3: Mark crop uploads as persisted (2-phase commit complete)
                for (const [slug, posState] of Object.entries(
                  useCanvasStateManager.getState().masterPositions
                )) {
                  if (posState.lastUploadedCropHash) {
                    useCanvasStateManager
                      .getState()
                      .setCropPersisted(slug, posState.lastUploadedCropHash);
                  }
                }
              } catch (configError) {
                const message = describeApiError(configError);
                console.error(
                  '[designStore] saveConfiguration failed:',
                  message
                );
                throw configError; // Outer catch sets syncError, keeps hasUnsavedChanges
              }
            }

            // 6. Design metadata and the design-level canvas, after the rows
            // The status is not echoed: the server keeps the stored one when
            // the field is absent, and only a publish sends it explicitly.
            const design = await designsApi.updateDesign(designId, {
              name: designName,
              description: designDescription || undefined,
              canvasData,
            });

            // A pass superseded by the wait timeout returns its result but
            // leaves the shared flags to the pass that replaced it.
            if (savePassSeq === token) {
              set({
                // A change made while the requests ran is not on the server.
                hasUnsavedChanges: get().changeVersion !== capturedVersion,
                lastSavedAt: new Date(),
                isSaving: false,
                rejectedAtVersion: null,
              });
            }

            // 7. Upload the thumbnail captured at the start (fire & forget)
            if (savePassSeq === token) {
              get().uploadThumbnail(designId, thumbnail);
            }

            return design;
          } catch (error) {
            const status = (error as { response?: { status?: number } })
              .response?.status;
            // Below the seller cost: the error names product and cost; say
            // where the price is set, because the Design step hides it.
            const hint =
              getApiErrorCode(error) === 'selling_price_below_base_cost'
                ? ` ${i18n.t('creator.toast.raisePriceHint')}`
                : '';
            if (savePassSeq === token) {
              set({
                syncError: describeApiError(error) + hint,
                hasUnsavedChanges: true,
                isSaving: false,
                rejectedAtVersion:
                  status === 400 || status === 422 ? capturedVersion : null,
              });
            }
            return null;
          }
        })();

        return trackRunningSave(capturedVersion, pass);
      },

      /**
       * Save changes for a single product only (single-product edit mode).
       *
       * Flow:
       * 1. Save current canvas state to canvasStateManager
       * 2. Build position-ID mapping for the target product
       * 3. For each position: PATCH via updateDesignPosition() (isolated, no other products)
       * 4. Save metadata (name, description) via updateDesign() WITHOUT canvasData
       * 5. Upload thumbnail (fire & forget)
       */
      saveSingleProduct: async () => {
        const { designId, designName, designDescription, canvas } = get();
        const { singleProductMode } = useUIStore.getState();

        if (!designId) {
          set({ syncError: 'Cannot save single product: no designId' });
          return null;
        }
        if (!singleProductMode) {
          set({
            syncError: 'Cannot save single product: not in single-product mode',
          });
          return null;
        }

        // Before the lock check, as in saveDesign: a coalesced call still
        // leaves the canvas state and a thumbnail behind for its trailing pass.
        if (canvas) {
          // The crop printed from is exported here; only yields when a
          // text's face is still loading (see ensureCanvasFontsLoaded).
          const fontsPending = ensureCanvasFontsLoaded(canvas);
          if (fontsPending) await fontsPending;
          useCanvasStateManager.getState().saveCurrentPosition();
        }
        const thumbnail = captureThumbnailForSave(canvas, designId);

        // Same coalescing as saveDesign: never drop this call's changes.
        if (get().isSaving) {
          queueThumbnailForTrailingSave(designId, thumbnail);
          return coalesceAfterSave('saveSingleProduct', () =>
            get().saveSingleProduct()
          );
        }

        const capturedVersion = get().changeVersion;
        const token = ++savePassSeq;
        set({ isSaving: true, syncError: null });

        const pass = (async (): Promise<Design | null> => {
          try {
            const { productId } = singleProductMode;

            // 1. Payload inputs before the first await: all position data and
            // the crops that still need an upload
            const allPositionData = useCanvasStateManager
              .getState()
              .getAllPositionData({ resolveMaster: false });

            const cropInputs: CropUploadInput[] = [];
            const masterPositions =
              useCanvasStateManager.getState().masterPositions;

            for (const [slug, posState] of Object.entries(masterPositions)) {
              if (posState.designCropDataUrl && posState.json?.objects) {
                cropInputs.push({
                  positionSlug: slug,
                  designCropDataUrl: posState.designCropDataUrl,
                  canvasObjects: posState.json.objects as Array<
                    Record<string, unknown>
                  >,
                  lastUploadedCropHash: posState.lastUploadedCropHash,
                });
              }
            }

            // 2. Build position-ID mapping for the target product
            // Source: product catalogue (authoritative for ProductPosition IDs)
            const apiProducts = await loadCatalogueForSave();
            const targetProduct = apiProducts.find((p) => p.id === productId);

            if (!targetProduct?.positions?.length) {
              throw new Error(
                `saveSingleProduct: product "${productId}" not found or has no positions`
              );
            }

            // Map: positionSlug -> productPositionId
            const positionIdBySlug: Record<string, string> = {};
            for (const pp of targetProduct.positions) {
              positionIdBySlug[pp.slug] = pp.id;
            }

            // 3. Upload design crops if dirty (reuse existing crop upload logic)
            if (cropInputs.length > 0) {
              const cropResults = await uploadDesignCrops(cropInputs, designId);

              // Update position state with S3 URLs and hashes
              for (const result of cropResults) {
                useCanvasStateManager
                  .getState()
                  .setCropUploadResult(
                    result.positionSlug,
                    result.s3Url,
                    result.contentHash
                  );
              }

              // Re-read position data after crop uploads (now contains S3 URLs)
              const updatedPositionData = useCanvasStateManager
                .getState()
                .getAllPositionData({ resolveMaster: false });
              Object.assign(allPositionData, updatedPositionData);
            }

            // 4. Save each position via updateDesignPosition() (isolated per-product)
            for (const [slug, data] of Object.entries(allPositionData)) {
              const productPositionId = positionIdBySlug[slug];
              if (!productPositionId) {
                console.warn(
                  `saveSingleProduct: no productPositionId for slug "${slug}" on product "${productId}", skipping`
                );
                continue;
              }

              // In single-product mode the canvas is the source of truth:
              // its freshly computed state on this product's own area, never
              // the per-product overrides of the drag interface.
              const row = data.json
                ? positionToMm(data.json as PositionJson, data.printContext)
                : null;

              await designsApi.updateDesignPosition(
                designId,
                productId,
                productPositionId,
                {
                  canvasData: (row?.canvasData ?? {
                    version: '6.0.0',
                    objects: [],
                  }) as Record<string, unknown>,
                  ...(row?.placementMm && { placementMm: row.placementMm }),
                  designCropUrl: data.designCropUrl ?? undefined,
                  isCustomPlacement: true,
                }
              );
            }

            // Mark crop hashes as persisted
            for (const [slug, posState] of Object.entries(
              useCanvasStateManager.getState().masterPositions
            )) {
              if (posState.lastUploadedCropHash) {
                useCanvasStateManager
                  .getState()
                  .setCropPersisted(slug, posState.lastUploadedCropHash);
              }
            }

            // 5. Save metadata via updateDesign() WITHOUT canvasData (master stays unchanged)
            // Status not echoed, as in saveDesign: the server keeps it.
            const design = await designsApi.updateDesign(designId, {
              name: designName,
              description: designDescription,
              // NOTE: canvasData intentionally omitted — master must not be overwritten
              // in single-product mode. Only this product's DesignPosition is updated.
            });

            // A pass superseded by the wait timeout returns its result but
            // leaves the shared flags to the pass that replaced it.
            if (savePassSeq === token) {
              set({
                // A change made while the requests ran is not on the server.
                hasUnsavedChanges: get().changeVersion !== capturedVersion,
                lastSavedAt: new Date(),
                isSaving: false,
                rejectedAtVersion: null,
              });
            }

            // 6. Update productDesignCrops so ProductCard shows the edited design
            const updatedMasterPositions =
              useCanvasStateManager.getState().masterPositions;
            const cropsBySlug: Record<PositionKey, ProductDesignCrop> = {};
            for (const [slug, posState] of Object.entries(
              updatedMasterPositions
            )) {
              const cropUrl =
                posState.designCropUrl ?? posState.designCropDataUrl ?? null;
              if (cropUrl && posState.relativeState) {
                cropsBySlug[slug] = {
                  cropUrl,
                  relativeState: posState.relativeState,
                };
              }
            }
            if (Object.keys(cropsBySlug).length > 0) {
              const { productDesignCrops: existingCrops } =
                useCanvasStateManager.getState();
              useCanvasStateManager.getState().setAllProductDesignCrops({
                ...existingCrops,
                [productId]: cropsBySlug,
              });
            }

            // 7. Upload the thumbnail captured at the start (fire & forget)
            if (savePassSeq === token) {
              get().uploadThumbnail(designId, thumbnail);
            }

            return design;
          } catch (error) {
            const message = describeApiError(error);
            if (savePassSeq === token) {
              set({
                syncError: message,
                hasUnsavedChanges: true,
                isSaving: false,
              });
            }
            return null;
          }
        })();

        return trackRunningSave(capturedVersion, pass);
      },

      /**
       * Create a new design in the backend
       * Uses Fabric.js canvasData as Source of Truth
       * Also saves product configuration and position-specific canvas data
       */
      createDesign: async (name?: string) => {
        const { canvas, designName, designDescription } = get();
        const dimensions = get().getCanvasDimensions();

        // Before the lock check, as in saveDesign: the canvas state reaches
        // masterPositions even when this call coalesces, and the thumbnail
        // is captured while the canvas is still mounted.
        if (canvas) {
          // The crop printed from is exported here; only yields when a
          // text's face is still loading (see ensureCanvasFontsLoaded).
          const fontsPending = ensureCanvasFontsLoaded(canvas);
          if (fontsPending) await fontsPending;
          useCanvasStateManager.getState().saveCurrentPosition();
        }
        const thumbnail = canvas ? captureThumbnail(canvas) : undefined;

        // A create while another pass runs must not create a second design:
        // once the lock frees, save into the design that pass produced.
        if (get().isSaving) {
          return coalesceAfterSave('createDesign', () => {
            const { designId: currentId } = get();
            if (currentId) return get().saveDesign();

            // Still no design id: the pass that held the lock never created
            // a row (createDesign stores the id right after the create
            // call). Creating here would race the user's own retry, so this
            // one caller is dropped instead - the user can save again.
            console.warn(
              '[designStore] createDesign: no design id after the running pass, not creating a second one'
            );
            return Promise.resolve(null);
          });
        }

        const capturedVersion = get().changeVersion;
        const token = ++savePassSeq;
        set({ isSaving: true, syncError: null });

        const pass = (async (): Promise<Design | null> => {
          try {
            // 1. Every payload input is read before the first await, so the
            // canvas JSON and the product selection come from one moment.
            const allPositionData = useCanvasStateManager
              .getState()
              .getAllPositionData();

            // The design-level canvas in millimetres, as in saveDesign
            const canvasData = designLevelCanvas();

            const csmCreate = useCanvasStateManager.getState();
            const {
              placementsByProduct: createPlacements,
              productDesignCrops: createCrops,
            } = csmCreate;
            const productIds = collectProductIdsForSave();
            const productConfig = useProductSelectionStore
              .getState()
              .getAllProductsConfig(productIds);

            const cropInputsCreate: CropUploadInput[] = [];
            for (const [slug, posState] of Object.entries(
              csmCreate.masterPositions
            )) {
              if (posState.designCropDataUrl && posState.json?.objects) {
                cropInputsCreate.push({
                  positionSlug: slug,
                  designCropDataUrl: posState.designCropDataUrl,
                  canvasObjects: posState.json.objects as Array<
                    Record<string, unknown>
                  >,
                  lastUploadedCropHash: posState.lastUploadedCropHash,
                });
              }
            }

            // 2. Create the design row
            const design = await designsApi.createDesign({
              // Resolved here, not at module load: i18next is not initialised
              // yet when the store module is evaluated.
              name: name || designName || i18n.t('creator.untitledDesign'),
              description: designDescription || undefined,
              canvasData,
              canvasWidth: dimensions.width,
              canvasHeight: dimensions.height,
              backgroundColor: 'transparent',
              // A guided design re-opens in the guided entry (see loadDesign).
              createdVia:
                useUIStore.getState().creatorEntryMode === 'guided'
                  ? 'guided'
                  : undefined,
            });

            // The row exists: remember it now, so a failure in the follow-up
            // writes is an ordinary save error and the retry goes through
            // saveDesign into this row instead of creating a second one.
            set({ designId: design.id, designName: design.name });

            // 3. Upload dirty design crops to S3 (Stufe 4.3)
            // Same pattern as saveDesign() — must happen before buildEnrichedProductConfig.
            if (cropInputsCreate.length > 0) {
              const cropResults = await uploadDesignCrops(
                cropInputsCreate,
                design.id
              );

              for (const result of cropResults) {
                useCanvasStateManager
                  .getState()
                  .setCropUploadResult(
                    result.positionSlug,
                    result.s3Url,
                    result.contentHash
                  );
              }

              // Re-read position data after crop uploads (now contains S3 URLs)
              const updatedPositionData = useCanvasStateManager
                .getState()
                .getAllPositionData();
              Object.assign(allPositionData, updatedPositionData);
            }

            // 4. Save the rows of the master, the active selection and the
            // custom placements (same set as saveDesign)
            if (productConfig.length > 0) {
              const apiProducts = await loadCatalogueForSave();
              const enrichedProductConfig = buildEnrichedProductConfig(
                productConfig,
                allPositionData,
                apiProducts,
                createPlacements,
                createCrops
              );

              // Full-Fail (Stufe 1.2): same pattern as saveDesign() above.
              try {
                const masterForSave = resolveMasterForSave();
                await designsApi.saveDesignConfiguration(design.id, {
                  products: enrichedProductConfig,
                  masterProductId: masterForSave,
                });
                rememberMasterAfterSave(masterForSave);
                await adoptPricingAfterSave(design.id);

                // Stufe 4.3: Mark crop uploads as persisted (2-phase commit complete)
                for (const [slug, posState] of Object.entries(
                  useCanvasStateManager.getState().masterPositions
                )) {
                  if (posState.lastUploadedCropHash) {
                    useCanvasStateManager
                      .getState()
                      .setCropPersisted(slug, posState.lastUploadedCropHash);
                  }
                }
              } catch (configError) {
                const message = describeApiError(configError);
                console.error(
                  '[designStore] saveConfiguration failed:',
                  message
                );
                throw configError; // Outer catch sets syncError, keeps hasUnsavedChanges
              }
            }

            // A pass superseded by the wait timeout returns its result but
            // leaves the shared flags to the pass that replaced it.
            if (savePassSeq === token) {
              set({
                // A change made while the requests ran is not on the server.
                hasUnsavedChanges: get().changeVersion !== capturedVersion,
                lastSavedAt: new Date(),
                isSaving: false,
                rejectedAtVersion: null,
              });
            }

            // 5. Upload the thumbnail captured at the start (fire & forget)
            if (savePassSeq === token) {
              get().uploadThumbnail(design.id, thumbnail);
            }

            return design;
          } catch (error) {
            const message = describeApiError(error);
            if (savePassSeq === token) {
              set({
                syncError: message,
                hasUnsavedChanges: true,
                isSaving: false,
              });
            }
            return null;
          }
        })();

        return trackRunningSave(capturedVersion, pass);
      },

      /**
       * Delete current design from the backend
       */
      deleteDesign: async () => {
        const { designId } = get();
        if (!designId) return false;

        set({ isLoading: true, syncError: null });
        try {
          await designsApi.deleteDesign(designId);
          get().clearDesign();
          set({ isLoading: false });
          return true;
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'Failed to delete design';
          set({ syncError: message, isLoading: false });
          return false;
        }
      },

      /**
       * Upload thumbnail for a design (fire & forget)
       * Generates a WebP thumbnail from the current canvas and uploads it.
       * Non-blocking - errors are logged but don't affect save operation.
       */
      uploadThumbnail: async (designId, thumbnail) => {
        try {
          // The capture a save pass started while its canvas was mounted
          // wins; without one the canvas must still be mounted now.
          const { canvas } = get();
          const blob = await (thumbnail ??
            (canvas ? captureThumbnail(canvas) : null));

          if (!blob) {
            // No canvas or no image - skip silently
            return;
          }

          // Upload to backend (fire & forget)
          await designsApi.uploadDesignThumbnail(designId, blob);
        } catch {
          // Thumbnail upload failed - continue silently (non-blocking operation)
        }
      },
    }),
    { name: 'design-store' }
  )
);
